import { ChevronLeft } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { AccountNumberParams } from 'pages/managePricing/managePricingResolver';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ROUTES } from 'routing';
import { CustomToolbar } from 'shared/components';
import { GridColumns } from 'shared/types';
import { ApprovalLevelRequirementEnum, ApprovalStatusEnum } from 'shared/constants';
import {
    useApproverUsers,
    useColumns,
    useConfirmDeviationRequest,
    useSendDeviationNeedsApprovalEmail,
} from 'shared/hooks';
import UseDynamicUrl from 'shared/hooks/useDynamicUrl';
import { useDistributor } from 'shared/providers';
import { DynamicEmailData } from 'shared/services/email-address-service';
import { useAuthState } from 'shared/stores';
import {
    determineApprovalLevelRequirement,
    determineApprovers,
    determineVariance,
    formatDatePickerStringWithoutTime,
    formatFourDecimalMoney,
    mapConfirmDeviationsFormData,
    parseDatePickerDate,
} from 'shared/utility';
import { moneyFourDecimalFormatter } from 'shared/utility/dataGrid';
import { ApproverUser, NeedsApprovalEmailData } from 'types/backend';
import { AddModelCurrentPricesForm, DeviationsConfirmationRow, TAddedDeviation } from 'types/forms';

interface ConfirmDeviationsProps {
    form: AddModelCurrentPricesForm;
    customerName: string;
}

export function ConfirmDeviations({ form, customerName }: ConfirmDeviationsProps) {
    const { handleSubmit } = useForm<AddModelCurrentPricesForm>();
    const {
        data: approverUsers,
        isLoading: approverUsersIsLoading,
        isFetching: approverUsersIsFetching,
    } = useApproverUsers();
    const isApproverUsersLoading = approverUsersIsLoading || approverUsersIsFetching;
    const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
    const backHandler = UseDynamicUrl<AccountNumberParams>({ route: ROUTES.addDeviationsSetup });
    const { onSubmit, isLoading } = useConfirmDeviationRequest();
    const authState = useAuthState();
    const currentUser = authState.user.get()?.displayName;
    const url = window.location.origin;
    const distributor = useDistributor();

    const dynamicEmailData: Omit<DynamicEmailData, 'currentUserName' | 'currentUserEmailAddress'> = {
        territorySalesLeaderName: distributor.matchingCustomer?.territorySalesLeader || '',
        territorySalesLeaderEmail: distributor.matchingCustomer?.territorySalesLeaderEmail || '',
        territorySalesRepName: distributor.matchingCustomer?.territorySalesRep || '',
        territorySalesRepEmail: distributor.matchingCustomer?.territorySalesRepEmail || '',
    };

    const sendEmail = useSendDeviationNeedsApprovalEmail(`${url}/approvals`, dynamicEmailData);

    const emailData: NeedsApprovalEmailData[] = form.addedDeviations.map((product) => {
        const approvalLevel = determineApprovalLevelRequirement(
            product.selectedProduct.tierOnePrice,
            Number(product.modelCurrentPrice.price),
            distributor.matchingCustomer.priceTier
        );
        const approvers = determineApprovers(approverUsers, approvalLevel);
        return {
            createdBy: currentUser ?? '',
            customerName: customerName,
            customerAccount: product.modelCurrentPrice.accountnumber,
            endUserName: product.endUserDisplay,
            endUserAccount: product.modelCurrentPrice.enduseraccountnumber,
            // addedDeviations.${index}.modelCurrentPrice.enduserpricinggroup
            pricingGroup: product.modelCurrentPrice.enduserpricinggroup,
            isOneTimeOnly: product.deviationDetails.isOneTimeOnly,
            productId: product.modelCurrentPrice.itemorgroup,
            customerTier: product.selectedProduct.customerTier,
            territorySalesLeader: distributor.matchingCustomer?.territorySalesLeader ?? 'Not Found',
            newPrice: formatFourDecimalMoney(Number(product.modelCurrentPrice.price)),
            customerTierTargetPrice: formatFourDecimalMoney(product.selectedProduct.tierOnePrice),
            currentPrice: formatFourDecimalMoney(product.selectedProduct.price),
            variance: determineVariance(
                Number(product.modelCurrentPrice.price),
                Number(product.selectedProduct.targetPrice)
            ),
            estAnnualVolume: product.modelCurrentPrice.estmonthlyrevenue,
            deviationReason: product.deviationDetails.deviationReason,
            comments: product.deviationReasonNotes ?? '',
            approverEmails: approvers.map((a) => a.email),
            statusDetails: {
                isApproved: approvalLevel === ApprovalLevelRequirementEnum.NOTREQUIRED,
                approver: approvers[0]?.name || '',
                status:
                    approvalLevel === ApprovalLevelRequirementEnum.NOTREQUIRED
                        ? ApprovalStatusEnum.APPROVED
                        : ApprovalStatusEnum.NEEDSAPPROVAL,
            },
            approverLevel: approvalLevel.valueOf(),
        };
    });

    const setStatusForModelCurrentPricesForm = (
        form: AddModelCurrentPricesForm,
        approverUsers: ApproverUser[] | undefined
    ): AddModelCurrentPricesForm => {
        const addedDeviations: TAddedDeviation[] = form.addedDeviations.map((deviation) => {
            const matchingEmailData = emailData.find((anEmail) => {
                return anEmail.productId === deviation.selectedProduct.productId;
            });
            return {
                ...deviation,
                modelCurrentPrice: {
                    ...deviation.modelCurrentPrice,
                    status: matchingEmailData?.statusDetails?.status ?? 'Approved',
                    approver: matchingEmailData?.statusDetails?.approver ?? 'Not Required',
                },
            };
        });
        return { ...form, addedDeviations };
    };

    const confirmClickHandler = handleSubmit(async () => {
        const needsEmail = emailData.filter((aRequest) => {
            return aRequest.statusDetails?.isApproved === false;
        });
        const currentPricesForm = setStatusForModelCurrentPricesForm(form, approverUsers); // set approval status according to tiers criteria
        onSubmit(currentPricesForm);
        await sendEmail.mutateAsync(needsEmail);
    });

    const columnConfig: GridColumns<DeviationsConfirmationRow> = [
        {
            headerName: 'Product',
            field: 'productId',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Deviation Type',
            field: 'deviationType',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'End User',
            field: 'endUser',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'Pricing Group',
            field: 'pricingGroup',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'One Time Only',
            field: 'isOneTimeOnly',
            hideable: false,
            pinnable: false,
            type: 'boolean',
            width: 100,
        },
        {
            headerName: 'T1 Target Price',
            field: 'tierOnePrice',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 100,
        },
        {
            headerName: 'Customer Target Price',
            field: 'targetPrice',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 100,
        },
        {
            headerName: 'Market Price',
            field: 'marketPrice',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 100,
        },
        {
            headerName: 'Deviated Price',
            field: 'deviatedPrice',
            hideable: false,
            pinnable: false,
            valueFormatter: moneyFourDecimalFormatter,
            type: 'number',
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Start Date',
            field: 'effectiveStartDate',
            hideable: false,
            pinnable: false,
            width: 200,
            type: 'date',
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
        },
        {
            headerName: 'End Date',
            field: 'endDate',
            hideable: false,
            pinnable: false,
            width: 200,
            type: 'date',
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
        },
        {
            headerName: 'Status',
            field: 'status',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Approver',
            field: 'approver',
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
    ];

    const columns = useColumns<DeviationsConfirmationRow>({ columnConfig });

    return (
        <>
            {!isApproverUsersLoading ? (
                <>
                    <DataGridPremium
                        autoHeight
                        getRowId={(row) => row.productId}
                        columns={columns}
                        rows={mapConfirmDeviationsFormData(form, approverUsers, distributor.matchingCustomer.priceTier)}
                        components={{
                            Toolbar: CustomToolbar,
                        }}
                        componentsProps={{
                            panel: {
                                anchorEl: filterButtonEl,
                                sx: {
                                    '& .MuiDataGrid-filterFormColumnInput': {
                                        width: 'auto',
                                    },
                                },
                            },
                            toolbar: {
                                setFilterButtonEl,
                                hideExport: true,
                                title: 'Confirm Changes',
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant={'contained'}
                            sx={{
                                backgroundColor: 'secondary.main',
                                color: 'black',
                                '&:hover': {
                                    backgroundColor: 'secondary.light',
                                    color: 'black',
                                },
                            }}
                            onClick={backHandler.clickHandler}
                            startIcon={<ChevronLeft />}
                        >
                            Back
                        </Button>
                        <Button
                            id='save'
                            sx={{
                                minWidth: '8rem',
                                backgroundColor: 'secondary.main',
                                color: 'black',

                                '&:hover': {
                                    backgroundColor: 'secondary.light',
                                    color: 'black',
                                },
                            }}
                            variant='contained'
                            onClick={confirmClickHandler}
                            disabled={isLoading}
                        >
                            {isLoading ? <CircularProgress size={30} /> : 'Save'}
                        </Button>
                    </Box>
                </>
            ) : (
                <CircularProgress />
            )}
        </>
    );
}
